import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import './Header.css';
import PropTypes from 'prop-types';
import MenuIcon from '@material-ui/icons/Menu';
import {
  Avatar,
  ClickAwayListener,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import BuildIcon from '@material-ui/icons/Build';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import CampaignIcon from '@material-ui/icons/Announcement';
import { showingMenuItemsSelector } from '../../redux/selectors/global';
import withShipment from '../../withShipment';
import { hideMenuItems, showMenuItems } from '../../redux/actions/global';
import { AccountCircle, Announcement } from '@material-ui/icons';
import Drawer from '@material-ui/core/Drawer';
import LeftMenu from '../leftmenu/LeftMenu';
import Divider from '@material-ui/core/Divider';
import {
  onlineEmployeesSelector,
  userInfoSelector,
  usersSelector,
} from '../../redux/selectors/auth';
import Utils from '../Utils/Utils';
import {
  authLogout,
  getOnlineEmployees,
  getUsers,
  updateSelectedUserId,
  updateUserInfo,
} from '../../redux/actions/auth';
import { protectedRoutes } from '../../routes';
import { ROUTE_TYPE } from '../../routes/constants';
import Alerts from '../alerts/Alerts';
import {
  listAvailableCompanies,
  listNotes,
} from '../../redux/actions/settings';
import { notesSelector } from '../../redux/selectors/settings';
import { availableCompaniesSelector } from '../../redux/selectors/account';
import CompanyPicker from '../global/subcomponents/companySelectBox/CompanyPicker';
import { Box, Card, CardContent, Popper } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ShieldIcon from '../assets/shield.png';

import { ReactComponent as Logo } from './nube-data-black.svg';

// Roles that are exempt from the clock-in/out flow. Reading Guy and
// Security guards don't punch the customer-facing clock — they get
// their full sidebar regardless of isClockedIn / sessionId state.
// Heuristic: if a user has no ROLE_CUSTOMER (the dashboard customer
// access role), they're a back-office user and bypass clock-in.
const isClockInExempt = (user) => {
  if (!user || !user.authorities) return false;
  const auths = user.authorities.map((a) =>
    typeof a === 'string' ? a : a && a.authority
  );
  // Admins already bypass the clock-in branches earlier; we only
  // care about the non-admin path here.
  if (auths.includes('ROLE_ADMIN') || auths.includes('ROLE_MASTER_ADMIN')) {
    return true;
  }
  return !auths.includes('ROLE_CUSTOMER');
};

class Header extends Component {
  constructor(props) {
    super(props);
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    this.state = {
      showingProfileDropdown: false,
      employees: null,
      selectedEmployee: null,
      open: false,
      // The employee switcher must NEVER render on a phone/tablet, even
      // when the viewport reports >800px (landscape load, display zoom,
      // "Desktop site" mode) — see Utils.isMobileDevice().
      isMobile:
        window.matchMedia('(max-width: 800px)').matches ||
        isIOSSafari ||
        Utils.isMobileDevice(),
      countdown: 0,
    };
    this.anchorRef = React.createRef();
    this.prevOpen = React.createRef();
  }

  componentDidMount() {
    if (this.props.isAuthenticated) {
      if (this.state.isMobile) {
        document.addEventListener(
          'visibilitychange',
          this.handleVisibilityChange
        );
      }
    }

    this.props.getUsers();

    if (this.userIsAnAdmin()) {
      this.props.getOnlineEmployees({
        locationId: this.props.userInfo.selectedLocationId,
      });

      this.props.listAvailableCompanies();
    } else if (
      this.props.userInfo.companyIds &&
      this.props.userInfo.companyIds.length > 0
    ) {
      this.props.listAvailableCompanies();
    } else if (
      this.props.userInfo.selectedLocations &&
      this.props.userInfo.selectedLocations.length > 1
    ) {
      this.props.listAvailableCompanies();
    }

    if (this.props.userInfo?.routes?.includes('note')) {
      this.props.listNotes({
        locationId: this.props.userInfo.selectedLocationId,
      });
    }
  }

  componentWillUnmount() {
    if (this.props.isAuthenticated) {
      if (this.state.isMobile) {
        document.removeEventListener(
          'visibilitychange',
          this.handleVisibilityChange
        );
      }

      clearInterval(this.countdownInterval);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      this.props.announcementEnd &&
      this.props.announcementEnd !== prevProps.announcementEnd
    ) {
      // Clear any existing countdown interval
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      this.startAnnouncementCountdown();
    }

    if (
      prevProps.availableCompanies !== this.props.availableCompanies &&
      Array.isArray(this.props.availableCompanies)
    ) {
      let findCompany = this.props.availableCompanies.find(
        (company) =>
          company.companyId === this.props.userInfo.selectedLocationId
      );
      let defaultCompany = findCompany ? findCompany : {};
      let selectedLocationId = defaultCompany?.companyId;
      this.setState({
        selectedCompany: defaultCompany,
        selectedLocationId: selectedLocationId,
      });
    }

    if (
      prevState.showingProfileDropdown !== this.state.showingProfileDropdown
    ) {
      if (
        this.prevOpen.current === true &&
        this.state.showingProfileDropdown === false
      ) {
        this.anchorRef.current.focus();
      }
      this.prevOpen.current = this.state.showingProfileDropdown;
    }

    if (prevProps.refresh !== this.props.refresh) {
      this.props.setRefresh(false);
      this.props.getUsers();
    }

    if (prevProps.allUsers !== this.props.allUsers) {
      if (!this.userIsAnAdmin() && this.state.isMobile) {
        //mobile

        const filteredEmployees = this.props.allUsers.filter((employee) => {
          const employeeRoles = employee.authorities.map(
            (auth) => auth.authority
          );

          if (
            employeeRoles.includes('ROLE_ADMIN') ||
            employeeRoles.includes('ROLE_MASTER_ADMIN')
          ) {
            return false;
          }

          if (
            employeeRoles.length === 2 &&
            employeeRoles.includes('ROLE_DASHBOARD') &&
            employeeRoles.includes('ROLE_USER')
          ) {
            return false;
          }

          return true;
        });

        // Update routes based on clock-in status. Back-office roles
        // (Reading Guy, Security — anyone without ROLE_CUSTOMER) are
        // exempt from clock-in and keep their full route set.
        const updatedEmployees = filteredEmployees.map((user) => {
          const exempt = isClockInExempt(user);
          const updatedRoutes = exempt
            ? user.routes
            : user.isClockedIn === false
            ? ['check-in-out']
            : user.routes;
          return {
            ...user,
            routes: updatedRoutes,
          };
        });

        // Only keep the employee that matches userInfo.userId
        const matchedUser = updatedEmployees.filter(
          (employee) => employee.userId === this.props.userInfo.userId
        );

        this.setState({ employees: matchedUser }, () => {
          const selectedEmployee = matchedUser[0] || {
            ...this.props.userInfo,
            routes: ['check-in-out'],
          };

          // Remove authorities if needed
          delete selectedEmployee.authorities;

          this.setState(
            { employees: [selectedEmployee], selectedEmployee },
            () => {
              if (selectedEmployee) {
                this.props.updateSelectedUserId(selectedEmployee);
              } else {
                this.props.history.push('/CheckIn404');
              }
            }
          );
        });

        return;
      }

      //desktop
      if (this.userIsAnAdmin()) {
        this.setState({ selectedEmployee: this.props.userInfo });
        return;
      }

      const filteredEmployees = this.props.allUsers.filter((employee) => {
        const employeeRoles = employee.authorities.map(
          (auth) => auth.authority
        );

        // Check if employee has ROLE_ADMIN
        if (
          employeeRoles.includes('ROLE_ADMIN') ||
          employeeRoles.includes('ROLE_MASTER_ADMIN')
        ) {
          return false;
        }

        // Check if the employee has exactly two roles and both are ROLE_DASHBOARD & ROLE_USER
        if (
          employeeRoles.length === 2 &&
          employeeRoles.includes('ROLE_DASHBOARD') &&
          employeeRoles.includes('ROLE_USER')
        ) {
          return false;
        }

        return true;
      });

      const updatedEmployees = filteredEmployees.map((user) => {
        // Back-office roles (Reading Guy, Security — anyone without
        // ROLE_CUSTOMER) are exempt from clock-in and keep their full
        // route set regardless of online / clockedIn / sessionId state.
        if (isClockInExempt(user)) {
          return { ...user, routes: user.routes };
        }
        const updatedRoutes = user.isUserOnline
          ? user.isClockedIn
            ? user.routes // Case 1
            : user.sessionId
            ? ['check-in-out']
            : [] // Case 2
          : user.isClockedIn
          ? [
              ...(user.routes.includes('dashboard') ? ['dashboard'] : []),
              ...(user.routes.includes('customers') ? ['customers'] : []),
              ...(user.routes.includes('raffle') ? ['raffle'] : []),
              ...(user.routes.includes('role-privilege')
                ? ['role-privilege']
                : []),
            ] //
          : user.sessionId
          ? ['check-in-out']
          : [];
        return {
          ...user,
          routes: updatedRoutes,
        };
      });

      this.setState({ employees: updatedEmployees }, () => {
        if (this.props.userInfo.userId) {
          let selectedEmployee = this.state.employees.find(
            (emp) => emp.userId === this.props.userInfo.userId
          );

          if (!selectedEmployee) {
            selectedEmployee = updatedEmployees[0];
          }

          this.setState({ selectedEmployee }, () => {
            this.props.updateSelectedUserId(this.state.selectedEmployee);
          });
        }
      });
    }
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.props.getUsers();
    }
  };

  handleSelectEmployee = (user) => {
    this.setState({ selectedEmployee: user }, () => {
      this.props.updateSelectedUserId(this.state.selectedEmployee);
      window.location.reload();
    });
  };

  userIsAnAdmin = () => {
    const { userInfo } = this.props;
    const roles = userInfo?.authority || [];
    return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_MASTER_ADMIN');
  };

  handleMenuBarToggle = () => {
    const menuOpen = this.props.showingMenuItems;
    menuOpen ? this.props.hideMenuItems() : this.props.showMenuItems();
  };

  handleProfileDropdownOpen = () => {
    this.setState((prev) => ({ open: !prev.open }));
  };

  handleClickAway = () => {
    this.setState({ open: false });
  };

  leftMenuRoutes = () => {
    let displayedRoutes = [];
    const userRoutes = this.state.selectedEmployee?.routes
      ? [...this.state.selectedEmployee.routes]
      : [];

    let dividerCount = 0;
    const hasAccess = (route) => {
      if (userRoutes.includes(route.id)) return true;
      if (
        route.requiresAnyOf &&
        route.requiresAnyOf.some((id) => userRoutes.includes(id))
      ) {
        return true;
      }
      return false;
    };
    protectedRoutes.map((route) => {
      if (route.type === ROUTE_TYPE.DIVIDER) {
        if (dividerCount === 0) {
          displayedRoutes = [...displayedRoutes, route];
          dividerCount++;
        }
      } else if (hasAccess(route)) {
        displayedRoutes = [...displayedRoutes, route];
        dividerCount = 0;
      }
      return null;
    });
    return displayedRoutes;
  };

  startAnnouncementCountdown = () => {
    const { announcementEnd } = this.props;
    if (!announcementEnd) return;

    // announcementEnd example: "2025-09-28 11:00 p.m."
    const [datePart, hourMinute, meridiem] = announcementEnd.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    let [hour, minute] = hourMinute.split(':').map(Number);

    // Convert 12-hour to 24-hour
    if (meridiem.toLowerCase().includes('p') && hour < 12) hour += 12;
    if (meridiem.toLowerCase().includes('a') && hour === 12) hour = 0;

    // Create date in local timezone (Houston UTC-5)
    // If your server is in UTC, adjust offset if needed
    const endTime = new Date(year, month - 1, day, hour, minute);

    this.startCountdown(endTime);
  };

  startCountdown = (endTime) => {
    this.countdownInterval = setInterval(() => {
      const now = new Date().getTime();
      const distance = Math.max(
        0,
        Math.floor((endTime.getTime() - now) / 1000)
      ); // seconds
      this.setState({ countdown: distance });

      if (distance <= 0) clearInterval(this.countdownInterval);
    }, 1000);
  };

  formatCountdown = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  render() {
    const { onlineEmployees = [] } = this.props;
    const { open } = this.state;

    const hasCompanyIds =
      this.props.userInfo?.companyIds &&
      this.props.userInfo?.companyIds?.length > 0;

    const hasLocationAccess =
      this.props.userInfo?.selectedLocations &&
      this.props.userInfo?.selectedLocations?.length > 0;

    const hasMultipleLocations =
      this.props.userInfo?.selectedLocations &&
      this.props.userInfo?.selectedLocations?.length > 1;

    const roles = this.props.userInfo?.authority || [];
    const isAdminOrAbove =
      roles.includes('ROLE_OWNER') ||
      roles.includes('ROLE_TECH_ADMIN') ||
      roles.includes('ROLE_MASTER_ADMIN') ||
      roles.includes('ROLE_ADMIN');
    // Owners + NDS staff (master/tech admin) have global billing access —
    // they manage Stripe cards across companies and shouldn't be hidden
    // just because their user record has no linkedCompanies / selected
    // locations yet. The My Account header is their only entry into the
    // Payment Methods card.
    const isGlobalBillingRole =
      roles.includes('ROLE_OWNER') ||
      roles.includes('ROLE_TECH_ADMIN') ||
      roles.includes('ROLE_MASTER_ADMIN');
    // Owners carry every role, so filter them out — the maintenance
    // banner is specifically a tech-admin affordance.
    const isTechAdmin =
      roles.includes('ROLE_TECH_ADMIN') && !roles.includes('ROLE_OWNER');

    // For tech admins, every entry in availableCompanies that isn't NDS
    // or a TESTING sandbox is a customer location that granted them an
    // active Maintenance Permit — i.e. a site waiting on a service visit.
    const maintenanceSites = isTechAdmin
      ? (this.props.availableCompanies || []).filter((c) => {
          const name = (c && c.name) || '';
          if (name === 'NuVe System') return false;
          if (name.toLowerCase() === 'testing') return false;
          return true;
        })
      : [];
    const selectedLocationId = this.props.userInfo?.selectedLocationId;

    return (
      <>
        {isTechAdmin &&
          maintenanceSites.length > 0 &&
          (() => {
            const activeSite = maintenanceSites.find(
              (s) => s.companyId === selectedLocationId
            );
            const otherSites = maintenanceSites.filter(
              (s) => s.companyId !== selectedLocationId
            );
            const isMobile = this.state.isMobile;
            const switchToSite = (site) => {
              const userInfo = this.props.userInfo || {};
              this.props.updateUserInfo({
                ...userInfo,
                selectedLocationId: site.companyId,
                selectedLocationName:
                  site.name || userInfo.selectedLocationName,
                planName: site.planName,
              });
              setTimeout(() => {
                window.location.reload();
              }, 100);
            };
            return (
              <div
                style={{
                  background:
                    'linear-gradient(90deg, #0a1133 0%, #0f1847 60%, #001665 100%)',
                  color: '#e7ecff',
                  fontSize: 14,
                  padding: isMobile ? '10px 14px' : '6px 20px',
                  minHeight: isMobile ? 0 : 46,
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: isMobile ? 8 : 16,
                  flexWrap: 'wrap',
                  borderBottom: '1px solid rgba(212,175,55,0.35)',
                  letterSpacing: '0.4px',
                  fontFamily: 'inherit',
                }}
              >
                {/* Row 1: Brand label + count — on mobile also push count to the right */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexShrink: 0,
                    justifyContent: isMobile ? 'space-between' : 'flex-start',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <BuildIcon style={{ fontSize: 18, color: '#D4AF37' }} />
                    <span
                      style={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: '#D4AF37',
                        fontSize: isMobile ? 12 : 13,
                        letterSpacing: isMobile ? '1px' : '1.2px',
                      }}
                    >
                      Maintenance Access
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.8px',
                      padding: '3px 9px',
                      borderRadius: 3,
                      background: 'rgba(212,175,55,0.15)',
                      color: '#D4AF37',
                      border: '1px solid rgba(212,175,55,0.4)',
                      flexShrink: 0,
                    }}
                  >
                    {maintenanceSites.length}{' '}
                    {maintenanceSites.length === 1 ? 'SITE' : 'SITES'}
                  </span>
                </div>

                {/* Vertical divider — desktop only */}
                {!isMobile && (
                  <div
                    style={{
                      width: 1,
                      height: 20,
                      background: 'rgba(255,255,255,0.12)',
                      flexShrink: 0,
                    }}
                  />
                )}

                {/* Active site — visually dominant; full-width row on mobile */}
                {activeSite && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: isMobile ? '6px 10px' : '4px 12px',
                      borderRadius: 4,
                      background: 'rgba(5,150,105,0.15)',
                      border: '1px solid rgba(5,150,105,0.55)',
                      flexShrink: 0,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: '#10b981',
                        boxShadow: '0 0 0 0 rgba(16,185,129,0.7)',
                        animation: 'ndsPulse 2s infinite',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '1px',
                        color: '#6ee7b7',
                        flexShrink: 0,
                      }}
                    >
                      ON SITE
                    </span>
                    <span
                      style={{
                        fontSize: isMobile ? 14 : 15,
                        fontWeight: 700,
                        color: '#ffffff',
                        letterSpacing: '0.3px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {activeSite.name}
                      {activeSite.companyCode
                        ? ` (${activeSite.companyCode})`
                        : ''}
                    </span>
                  </div>
                )}

                {/* Other authorized sites — wraps on desktop, horizontal-scroll on mobile */}
                {otherSites.length > 0 && (
                  <div
                    className="nds-maint-authorized"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 8 : 6,
                      flexWrap: isMobile ? 'nowrap' : 'wrap',
                      flex: 1,
                      minWidth: 0,
                      overflowX: isMobile ? 'auto' : 'visible',
                      WebkitOverflowScrolling: 'touch',
                      paddingBottom: isMobile ? 2 : 0,
                      scrollbarWidth: 'none',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: '1px',
                        color: '#8a9cc5',
                        textTransform: 'uppercase',
                        flexShrink: 0,
                      }}
                    >
                      Authorized:
                    </span>
                    {otherSites.map((site) => (
                      <span
                        key={site.companyId}
                        onClick={() => switchToSite(site)}
                        title={`Switch to ${site.name}`}
                        style={{
                          cursor: 'pointer',
                          padding: isMobile ? '7px 13px' : '4px 12px',
                          borderRadius: 3,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.14)',
                          color: '#c7d2fe',
                          fontSize: 14,
                          fontWeight: 600,
                          letterSpacing: '0.2px',
                          transition: 'all 0.15s ease',
                          flexShrink: 0,
                          minHeight: isMobile ? 32 : 'auto',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            'rgba(212,175,55,0.12)';
                          e.currentTarget.style.borderColor =
                            'rgba(212,175,55,0.5)';
                          e.currentTarget.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            'rgba(255,255,255,0.04)';
                          e.currentTarget.style.borderColor =
                            'rgba(255,255,255,0.14)';
                          e.currentTarget.style.color = '#c7d2fe';
                        }}
                      >
                        {site.name}
                        {site.companyCode ? ` (${site.companyCode})` : ''}
                      </span>
                    ))}
                  </div>
                )}

                {/* Inline keyframes for the pulse dot + hide horizontal scrollbar on mobile */}
                <style>{`
                @keyframes ndsPulse {
                  0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
                  70%  { box-shadow: 0 0 0 6px rgba(16,185,129,0); }
                  100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
                }
                .nds-maint-authorized::-webkit-scrollbar { height: 0; width: 0; display: none; }
              `}</style>
              </div>
            );
          })()}
        {isGlobalBillingRole ||
        (isAdminOrAbove &&
          ((hasCompanyIds && this.props.userInfo?.selectedLocationName) ||
            hasLocationAccess)) ||
        hasMultipleLocations ? (
          <div
            style={{
              backgroundColor: '#001665',
              color: 'white',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 24px',
              letterSpacing: '0.5px',
            }}
          >
            {/* Left side: Shield + Name + Owner */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              onClick={this.props.openProfile}
            >
              <img
                src={ShieldIcon}
                alt="shield"
                style={{ width: '42px', height: '42px' }}
              />
              <div>
                <div style={{ fontSize: '13px' }}>My Account</div>
              </div>
            </div>

            {/* Right side: Company Picker */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              <CompanyPicker
                userInfo={this.props.userInfo}
                updateUserInfo={this.props.updateUserInfo}
                availableCompanies={this.props.availableCompanies}
              />
              {(() => {
                // Prefer the live company name over the one stamped on the
                // user record at login — that stamp can be stale (accounts
                // created before the rebrand still say "Nube Data System")
                // and is only refreshed after a manual company switch.
                const freshName = (this.props.availableCompanies || []).find(
                  (c) => c.companyId === this.props.userInfo.selectedLocationId
                )?.name;
                const locationName =
                  freshName || this.props.userInfo.selectedLocationName;
                // Both the current and legacy brand names display as NDS.
                const isNds =
                  locationName === 'NuVe System' ||
                  /^nube data system$/i.test(locationName || '');
                return locationName ? (
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 'lighter',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    {isNds ? 'NDS' : locationName}
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        ) : null}

        {this.props.serverStatus === 'DOWN' ? (
          <div
            style={{
              background:
                'linear-gradient(90deg, #2a0a0a 0%, #3a0f0f 60%, #4a1414 100%)',
              color: '#ffe4e4',
              fontSize: 14,
              padding: this.state.isMobile ? '10px 14px' : '6px 20px',
              minHeight: this.state.isMobile ? 0 : 46,
              display: 'flex',
              flexDirection: this.state.isMobile ? 'column' : 'row',
              alignItems: this.state.isMobile ? 'stretch' : 'center',
              gap: this.state.isMobile ? 6 : 12,
              flexWrap: 'wrap',
              borderBottom: '1px solid rgba(239,68,68,0.55)',
              letterSpacing: '0.4px',
              fontFamily: 'inherit',
            }}
          >
            {/* Brand block */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
                justifyContent: this.state.isMobile
                  ? 'space-between'
                  : 'flex-start',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: '#ef4444',
                    boxShadow: '0 0 0 0 rgba(239,68,68,0.7)',
                    animation: 'ndsPulseRed 1.6s infinite',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                <CloudOffIcon style={{ fontSize: 18, color: '#fca5a5' }} />
                <span
                  style={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#fca5a5',
                    fontSize: this.state.isMobile ? 12 : 13,
                    letterSpacing: this.state.isMobile ? '1px' : '1.2px',
                  }}
                >
                  Service Disruption
                </span>
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.8px',
                  padding: '3px 9px',
                  borderRadius: 3,
                  background: 'rgba(239,68,68,0.18)',
                  color: '#fecaca',
                  border: '1px solid rgba(239,68,68,0.5)',
                  flexShrink: 0,
                }}
              >
                OFFLINE
              </span>
            </div>

            {!this.state.isMobile && (
              <div
                style={{
                  width: 1,
                  height: 20,
                  background: 'rgba(255,255,255,0.12)',
                  flexShrink: 0,
                }}
              />
            )}

            <span
              style={{
                fontSize: this.state.isMobile ? 14 : 15,
                fontWeight: 500,
                color: '#ffffff',
                letterSpacing: '0.2px',
                flex: 1,
                minWidth: 0,
              }}
            >
              Server is restarting — please retry in about 10 minutes.
            </span>
          </div>
        ) : (
          this.props.announcementMessage && (
            <div
              style={{
                background:
                  'linear-gradient(90deg, #2a1a05 0%, #3a2508 60%, #4a3010 100%)',
                color: '#fff1d6',
                fontSize: 14,
                padding: this.state.isMobile ? '10px 14px' : '6px 20px',
                minHeight: this.state.isMobile ? 0 : 46,
                display: 'flex',
                flexDirection: this.state.isMobile ? 'column' : 'row',
                alignItems: this.state.isMobile ? 'stretch' : 'center',
                gap: this.state.isMobile ? 6 : 12,
                flexWrap: 'wrap',
                borderBottom: '1px solid rgba(245,158,11,0.55)',
                letterSpacing: '0.4px',
                fontFamily: 'inherit',
              }}
            >
              {/* Brand block */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexShrink: 0,
                  justifyContent: this.state.isMobile
                    ? 'space-between'
                    : 'flex-start',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CampaignIcon style={{ fontSize: 18, color: '#fbbf24' }} />
                  <span
                    style={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#fbbf24',
                      fontSize: this.state.isMobile ? 12 : 13,
                      letterSpacing: this.state.isMobile ? '1px' : '1.2px',
                    }}
                  >
                    Announcement
                  </span>
                </div>
                {this.state.countdown !== null && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.8px',
                      padding: '3px 9px',
                      borderRadius: 3,
                      background: 'rgba(245,158,11,0.18)',
                      color: '#fde68a',
                      border: '1px solid rgba(245,158,11,0.5)',
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}
                  >
                    {this.formatCountdown(this.state.countdown)}
                  </span>
                )}
              </div>

              {!this.state.isMobile && (
                <div
                  style={{
                    width: 1,
                    height: 20,
                    background: 'rgba(255,255,255,0.12)',
                    flexShrink: 0,
                  }}
                />
              )}

              <span
                style={{
                  fontSize: this.state.isMobile ? 14 : 15,
                  fontWeight: 500,
                  color: '#ffffff',
                  letterSpacing: '0.2px',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {this.props.announcementMessage}
              </span>
            </div>
          )
        )}
        {/* Shared keyframes for red pulse (server down) */}
        <style>{`
          @keyframes ndsPulseRed {
            0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
            70%  { box-shadow: 0 0 0 6px rgba(239,68,68,0); }
            100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
          }
        `}</style>

        <nav className="navbar sticky-top navbar-dark  top-nav">
          <div className="top-nav">
            <div className="menu-toggle-name">
              <IconButton onClick={this.handleMenuBarToggle}>
                <MenuIcon color="secondary" />
              </IconButton>
              <a className="navbar-brand" href={'/'}>
                <Logo className="navbar-logo" />
                <span className="brand-text">NuVe System</span>
              </a>
            </div>

            {window.screen.width <= 800 && (
              <Drawer
                anchor="left"
                open={this.props.showingMenuItems}
                onClose={this.props.hideMenuItems}
                className="menu-drawer"
                transitionDuration={{ enter: 300, exit: 200 }}
                ModalProps={{
                  keepMounted: true, // Improves performance
                }}
              >
                <div className="drawer-container">
                  {/* Drawer Header */}
                  <div className="drawer-header">
                    <a className="navbar-brand" href={'/'}>
                      <Logo className="navbar-logo" />
                      <span className="brand-text">NuVe System</span>
                    </a>

                    <div className="drawer-header-close-icon">
                      <Tooltip title="Close">
                        <IconButton
                          aria-label="close"
                          onClick={this.props.hideMenuItems}
                          className="close-icon-button"
                        >
                          <DoubleArrowIcon
                            style={{ color: 'white', marginRight: '8px' }}
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  <Divider style={{ marginBottom: '16px' }} />

                  {/* Left Menu */}
                  {this.state.selectedEmployee && (
                    <LeftMenu
                      isDrawer={true}
                      user={this.props.userInfo}
                      closeDrawer={this.props.hideMenuItems}
                      routes={this.leftMenuRoutes()}
                      logout={this.props.authLogout}
                      userRoutes={this.state.selectedEmployee.routes}
                    />
                  )}
                </div>
              </Drawer>
            )}

            {this.state.isMobile ? (
              <div className="nav-icons">
                {this.userIsAnAdmin() && (
                  <ClickAwayListener onClickAway={this.handleClickAway}>
                    <div>
                      <IconButton
                        ref={this.anchorRef}
                        aria-label="employee"
                        onClick={this.handleProfileDropdownOpen}
                      >
                        <AccountCircle
                          style={{
                            color: Utils.isValidColor(
                              this.props.userInfo?.employeeColor
                            )
                              ? this.props.userInfo.employeeColor
                              : '#7896ff',
                          }}
                          fontSize="large"
                        />
                      </IconButton>

                      <Popper
                        open={open}
                        anchorEl={this.anchorRef.current}
                        placement="bottom-start"
                        style={{ zIndex: 1300 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            mt: 1,
                          }}
                        >
                          {onlineEmployees.map((employee) => (
                            <Card
                              key={employee.userId}
                              sx={{
                                minWidth: 160,
                                maxWidth: 200,
                                borderRadius: 2,
                                boxShadow: 3,
                              }}
                            >
                              <CardContent
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                }}
                              >
                                <Avatar
                                  style={{
                                    backgroundColor:
                                      employee.employeeColor || '#2596be',
                                    color: '#fff',
                                  }}
                                >
                                  {employee.firstname
                                    ? employee.firstname.charAt(0)
                                    : '?'}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {employee.firstname}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <FiberManualRecordIcon
                                      fontSize="small"
                                      sx={{ color: 'green' }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Online
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </Popper>
                    </div>
                  </ClickAwayListener>
                )}

                <Alerts />
              </div>
            ) : (
              <div className="user-profiles">
                {this.userIsAnAdmin() ? (
                  <ClickAwayListener onClickAway={this.handleClickAway}>
                    <div>
                      <IconButton
                        ref={this.anchorRef}
                        aria-label="employee"
                        onClick={this.handleProfileDropdownOpen}
                      >
                        <Tooltip title={this.props.userInfo.firstname}>
                          <AccountCircle
                            style={{
                              color: Utils.isValidColor(
                                this.props.userInfo?.employeeColor
                              )
                                ? this.props.userInfo.employeeColor
                                : '#7896ff',
                            }}
                            fontSize="large"
                          />
                        </Tooltip>
                      </IconButton>

                      <Popper
                        open={open}
                        anchorEl={this.anchorRef.current}
                        placement="bottom-start"
                        style={{ zIndex: 1300 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            mt: 1,
                          }}
                        >
                          {onlineEmployees.map((employee) => (
                            <Card
                              key={employee.userId}
                              sx={{
                                minWidth: 200,
                                borderRadius: 3,
                                boxShadow: 4,
                              }}
                            >
                              <CardContent
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                }}
                              >
                                <Avatar
                                  style={{
                                    backgroundColor:
                                      employee.employeeColor || '#2596be',
                                    color: '#fff',
                                  }}
                                >
                                  {employee.firstname
                                    ? employee.firstname.charAt(0)
                                    : '?'}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {employee.firstname}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <FiberManualRecordIcon
                                      fontSize="small"
                                      sx={{ color: 'green' }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Online
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </Popper>
                    </div>
                  </ClickAwayListener>
                ) : this.state.employees && this.state.employees.length > 0 ? (
                  this.state.employees.map((employee) => (
                    <div className="profile">
                      <Tooltip
                        key={employee.userId}
                        title={employee.firstname}
                        placement="bottom"
                      >
                        <IconButton
                          onClick={() => {
                            this.handleSelectEmployee(employee);
                          }}
                          ref={this.anchorRef}
                          aria-label="delete"
                          style={{ padding: 4 }}
                        >
                          <Avatar
                            style={{
                              backgroundColor:
                                employee.employeeColor || '#7896ff',
                              color: '#fff',
                              width: 42,
                              height: 42,
                              fontWeight: 600,
                              border:
                                this.state.selectedEmployee === employee
                                  ? '3px solid white'
                                  : '1px solid rgba(0,0,0,0.2)',
                              transition: 'border 0.3s ease',
                            }}
                          >
                            {employee.firstname
                              ? employee.firstname.charAt(0).toUpperCase()
                              : '?'}
                          </Avatar>
                        </IconButton>
                      </Tooltip>
                    </div>
                  ))
                ) : (
                  <Tooltip title={this.props.firstname} placement={'bottom'}>
                    <IconButton ref={this.anchorRef}>
                      <AccountCircle
                        style={{
                          color: Utils.isValidColor(
                            this.props.userInfo?.employeeColor
                          )
                            ? this.props.userInfo.employeeColor
                            : '#7896ff',
                        }}
                        variant="outlined"
                        fontSize="large"
                      />
                    </IconButton>
                  </Tooltip>
                )}

                <Alerts />

                {this.props.userInfo?.routes?.includes('note') &&
                  this.props.notes?.length > 0 && (
                    <div className="note-alert">
                      <Tooltip title="You have a new notes">
                        <IconButton
                          onClick={() => this.props.history.push('/note')}
                        >
                          <span className="flashing-dot"></span>
                          <Announcement
                            color="secondary"
                            style={{ fontSize: '30px' }}
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
              </div>
            )}
          </div>
        </nav>
      </>
    );
  }
}

Header.propTypes = {
  logout: PropTypes.func,
  showingMenuItems: PropTypes.bool,
};

Header.defaultProps = {
  logout: () => {},
  refresh: false,
  showingMenuItems: false,
};

const mapStateToProps = (state) => ({
  showingMenuItems: showingMenuItemsSelector(state),
  userInfo: userInfoSelector(state) || {},
  allUsers: usersSelector(state),
  notes: notesSelector(state),
  availableCompanies: availableCompaniesSelector(state),
  onlineEmployees: onlineEmployeesSelector(state),
});

const actionCreators = {
  showMenuItems,
  hideMenuItems,
  getUsers,
  updateSelectedUserId,
  authLogout,
  listNotes,
  listAvailableCompanies,
  getOnlineEmployees,
  updateUserInfo,
};

export default withRouter(
  withShipment(
    {
      mapStateToProps,
      actionCreators,
    },
    Header
  )
);
